import { ConditionalDisplay } from "$components/conditional-display";
import { ErrorState } from "$components/error-state";
import { makeStyles } from "$components/mui-styles";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import useFacility from "$routes/_lib/use-facility";
import useCurrencyFormatter from "$routes/patient/[:patientId]/_lib/invoices/hooks/useCurrencyFormatter";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { usePermissions } from "../../../hooks/usePermissions";
import { EditSwitch } from "../../EditSwitch";
import { InvoiceStatus } from "../../InvoiceFilters";
import {
  DeleteInvoiceModal,
  ExemptModal,
  FinaliseInvoiceModal,
  PaymentHistoryModal,
  PrintErrorModal,
  PrintPermissionsModal,
  RefundInvoiceModal,
  TakePaymentModal,
  VoidInvoiceModal,
} from "../../modals";
import { PrintPayerSelectionModal } from "../../modals/PrintPayerSelectionModal";
import { useFetchInvoice } from "../hooks/useFetchInvoice";
import usePrintThermalReceipt from "../hooks/usePrintThermalReceipt";
import { PATIENT } from "../InsurerAccordion/formConstants";
import type { Payer } from "../InsurerAccordion/types";
import { InvoiceActions } from "../InvoiceActions";
import type { InvoiceDetailsProps } from "../types";
import { getDisplayOptions } from "./getDisplayOptions";
import {
  InvoiceDetailsActionType,
  type InvoiceDetailsReducerAction,
  type InvoiceDetailsReducerState,
  useInvoiceDetailsDispatcher,
} from "./hooks/useInvoiceDetailsDispatcher";
import { InsurerSection } from "./InsurerSection";
import { LineItemSection } from "./LineItemSection";
import { PatientTypeSection } from "./PatientTypeSection";
import { PrintableInvoice } from "./PrintableInvoice";
import { PrintableLaserReceipt } from "./PrintableLaserReceipt";

interface BalanceState {
  number: number;
  formatted: string;
}

const defaultBalanceState: BalanceState = {
  number: 0,
  formatted: "-",
};

export function calculateBalance(
  total: number,
  insuranceAmount: number,
  patientPayer: Payer | null,
  status: {
    paymentIsDue: boolean;
    hasLineItems: boolean;
  },
  currencyFormatter: (number) => string,
): BalanceState {
  if (status.hasLineItems && !status.paymentIsDue) {
    // nothing being due overrides all other calculations
    return { number: 0, formatted: currencyFormatter(0) };
  }
  const patientPaid = patientPayer
    ? patientPayer.balance?.paid +
      (patientPayer.balance?.exempt ?? 0) +
      (patientPayer.balance?.waived ?? 0)
    : 0;
  const calculatedBalance = total - insuranceAmount - patientPaid;
  const formattedBalance =
    calculatedBalance >= 0 && status.hasLineItems
      ? currencyFormatter(calculatedBalance)
      : "-";
  return { number: calculatedBalance, formatted: formattedBalance };
}

const stateReducer = (
  prevState: InvoiceDetailsReducerState,
  action: InvoiceDetailsReducerAction,
) => {
  switch (action.type) {
    case InvoiceDetailsActionType.Busy: {
      return { ...prevState, busy: Boolean(action.value ?? true) };
    }
    default:
      throw new Error(`invalid action: ${JSON.stringify(action)}`);
  }
};

const useStyles = makeStyles((theme) => ({
  missingPriceBanner: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.warning.contrastText,
  },
  missingPriceBannerIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1.1rem",
  },
}));

export const InvoiceDetails = ({
  invoiceId,
  priceList,
  insurers,
  setSummaryBalance,
  setSummaryTotal,
  isNewInvoice,
  facility,
}: InvoiceDetailsProps) => {
  const classes = useStyles();
  const { data: userFacility } = useFacility();

  const { currencyFormatter } = useCurrencyFormatter();
  const permissions = usePermissions();
  const [isEditMode, setIsEditMode] = useState<boolean>(isNewInvoice);
  const [deleteModal, setDeleteModal] = useState(false);
  const [voidModal, setVoidModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [exemptModal, setExemptModal] = useState(false);
  const [finaliseModal, setFinaliseModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [printPermissionsModal, setPrintPermissionModal] =
    useState<boolean>(false);
  const [printPayerSelectionModal, setPrintPayerSelectionModal] =
    useState<boolean>(false);
  const [printPayerNames, setPrintPayerNames] = useState<Array<string>>([]);
  const [printInvoice, setPrintInvoice] = useState(false);
  const [printLaserReceipt, setPrintLaserReceipt] = useState(false);
  const [hasLineItems, setHasLineItems] = useState<boolean>(null);
  const [paymentHistoryModal, setPaymentHistoryModal] = useState(false);
  const [total, setTotal] = useState<BalanceState>(defaultBalanceState);
  const [insuranceAmount, setInsuranceAmount] =
    useState<BalanceState>(defaultBalanceState);

  const { invoice, error } = useFetchInvoice(invoiceId);

  const [state, dispatcher] = useInvoiceDetailsDispatcher(stateReducer, {
    busy: false,
  });

  const {
    havePrinter,
    printReceipt,
    resetErrors: resetPrinterErrors,
    printerError,
    fetchError,
  } = usePrintThermalReceipt(invoiceId);

  const patientInsurance = invoice?.patient?.insurance;
  const invoicePayers = invoice?.payers || [];
  const payers = invoicePayers.filter((p) => p.type !== PATIENT);
  const patientPayer = invoicePayers.find((p) => p.type === "PATIENT");

  const hasRefundableBalance = (patientPayer?.balance?.paid ?? 0) > 0;
  // `missingPrice` is a billing-2.0 (rxdb-billing) concept. When that flag is
  // off the user is on the v1 invoice UI, which has no way to resolve a
  // missing price — so the warning banner and the finalise block it drives
  // must not surface here. Gate on the flag so they only show once 2.0 is
  // rolled out. See PNE-522.
  const [rxdbBillingEnabled] = useFeatureFlag<boolean>(
    featureFlags.billing.rxdbBilling,
  );
  const hasMissingPriceItems = Boolean(
    rxdbBillingEnabled &&
      (invoice?.lineItems ?? []).some((l) => l.missingPrice && !l.disabled),
  );

  const invoiceActions = {
    displayOptions: getDisplayOptions(invoice),
    printInvoice: {
      handlePrintInvoice: () => {
        if (invoicePayers.length > 1) {
          setPrintPayerSelectionModal(true);
        } else {
          setPrintInvoice(true);
        }
      },
      handlePrintReceipt: async () => {
        const printerAvailable = await havePrinter();
        if (printerAvailable) {
          await printReceipt();
        } else {
          setPrintPermissionModal(true);
        }
      },
      disabled: isEditMode,
    },
    deleteInvoice: { handleAction: () => setDeleteModal(true) },
    takePayment: { handleAction: () => setPaymentModal(true) },
    voidInvoice: { handleAction: () => setVoidModal(true) },
    refundInvoice: {
      handleAction: () => setRefundModal(true),
      hide: !hasRefundableBalance,
    },
    paymentHistory: { handleAction: () => setPaymentHistoryModal(true) },
    finaliseInvoice: {
      handleAction: () => setFinaliseModal(true),
      hide: isEditMode,
      disabled: hasMissingPriceItems,
    },
  };

  const invoiceBalance: BalanceState = useMemo(
    () =>
      calculateBalance(
        total.number,
        insuranceAmount.number,
        patientPayer,
        { paymentIsDue: invoice?.paymentIsDue, hasLineItems },
        currencyFormatter,
      ),
    [
      total.number,
      insuranceAmount.number,
      invoice?.paymentIsDue,
      patientPayer,
      hasLineItems,
      currencyFormatter,
    ],
  );

  const totalPayableInPence = useMemo(
    () => invoice?.payers?.find((p) => p.type === "PATIENT")?.balance?.due ?? 0,
    [invoice?.payers],
  );

  const handleSetTotal = useCallback(
    (totalToSet: number) => {
      const formattedTotal = currencyFormatter(totalToSet);
      setTotal({ number: totalToSet, formatted: formattedTotal });
      setSummaryTotal(formattedTotal);
    },
    [currencyFormatter, setSummaryTotal],
  );

  const handleSetInsuranceAmount = useCallback(
    (paidToSet: number) => {
      const formattedPaid = currencyFormatter(paidToSet || 0);
      setInsuranceAmount({ number: paidToSet, formatted: formattedPaid });
    },
    [currencyFormatter],
  );

  useEffect(() => {
    setSummaryBalance(invoiceBalance.formatted);
  }, [invoiceBalance, setSummaryBalance]);

  if (!invoice || !priceList)
    return (
      <Box p={2} width="100%" textAlign="center">
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box p={3}>
        <ErrorState variant="inline" error={error} />
      </Box>
    );

  const hasLineItemsFromFetch = invoice.lineItems.length > 0;
  const userCanEditInvoice = userFacility?.id === facility.id;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box textAlign="right">
          <ConditionalDisplay
            condition={invoice?.invoiceStatus === InvoiceStatus.Draft}
          >
            {userCanEditInvoice && (
              <EditSwitch
                editable={isEditMode}
                toggleEdit={setIsEditMode}
                disabled={state.busy}
              />
            )}
          </ConditionalDisplay>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <PatientTypeSection
          invoiceId={invoiceId}
          editable={isEditMode}
          dispatcher={dispatcher}
          busy={state.busy}
        />
      </Grid>
      <ConditionalDisplay condition={hasMissingPriceItems}>
        <Grid item xs={12}>
          <div className={classes.missingPriceBanner}>
            <WarningIcon className={classes.missingPriceBannerIcon} />
            <Typography variant="body2">
              <FormattedMessage
                id="invoicing.invoice.missingPrice.warning"
                defaultMessage="One or more items have no price set. Please set a price or remove them before finalising."
              />
            </Typography>
          </div>
        </Grid>
      </ConditionalDisplay>
      <Grid item xs={12}>
        <LineItemSection
          invoiceId={invoiceId}
          invoice={invoice}
          priceList={priceList}
          editable={isEditMode}
          setHasLineItems={setHasLineItems}
          hasLineItems={hasLineItems ?? hasLineItemsFromFetch}
          total={total.number}
          setTotal={handleSetTotal}
          dispatcher={dispatcher}
          busy={state.busy}
        />
      </Grid>
      <Grid item xs={12}>
        <InsurerSection
          canAddInsurers
          hasLineItems={hasLineItems ?? hasLineItemsFromFetch}
          payers={payers}
          insurers={insurers}
          totalAmount={total.number}
          invoiceId={invoiceId}
          patientInsurance={patientInsurance}
          editable={isEditMode}
          paid={insuranceAmount.number}
          setPaid={handleSetInsuranceAmount}
          isDraft={invoice?.invoiceStatus === InvoiceStatus.Draft}
          dispatcher={dispatcher}
          busy={state.busy}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
        <Box py={2} textAlign="right">
          <Box display="inline-block" pr={1}>
            <Typography variant="body2" gutterBottom>
              <FormattedMessage
                id="invoicing.invoice.details.insurers"
                defaultMessage="Insurance"
              />
              {": "}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <FormattedMessage
                id="invoicing.invoice.details.balance"
                defaultMessage="Balance"
              />
              {": "}
            </Typography>
            <Typography gutterBottom>
              <FormattedMessage
                id="invoicing.invoice.details.total"
                defaultMessage="Total"
              />
              {": "}
            </Typography>
          </Box>
          <Box display="inline-block">
            <Typography variant="body2" gutterBottom>
              <strong>{insuranceAmount.formatted}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>{invoiceBalance.formatted}</strong>
            </Typography>
            <Typography gutterBottom>
              <strong>{total.formatted}</strong>
            </Typography>
          </Box>
        </Box>
      </Grid>
      {userCanEditInvoice && (
        <Grid item xs={12}>
          <InvoiceActions {...invoiceActions} />
        </Grid>
      )}
      <ConditionalDisplay condition={deleteModal}>
        <DeleteInvoiceModal
          open={deleteModal}
          onClose={() => setDeleteModal(false)}
          invoiceId={invoiceId}
        />
      </ConditionalDisplay>

      <ConditionalDisplay condition={finaliseModal}>
        <FinaliseInvoiceModal
          invoiceId={invoiceId}
          open={finaliseModal}
          onClose={() => setFinaliseModal(false)}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={voidModal}>
        <VoidInvoiceModal
          invoiceId={invoiceId}
          open={voidModal}
          onClose={() => setVoidModal(false)}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={paymentModal}>
        <TakePaymentModal
          invoiceId={invoiceId}
          currency={invoice.currencyCode}
          totalPayableInPence={patientPayer?.balance?.due ?? 0}
          waiveable={permissions.waive}
          exemptable={
            permissions.exemption &&
            // can only exempt if invoice has only patient payer
            invoice?.payers?.every((p) => p.type === "PATIENT")
          }
          open={paymentModal}
          onClose={() => setPaymentModal(false)}
          openExempt={() => setExemptModal(true)}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={exemptModal}>
        <ExemptModal
          open={exemptModal}
          onClose={() => {
            setExemptModal(false);
            setPaymentModal(false);
          }}
          onCancel={() => {
            setExemptModal(false);
          }}
          invoiceId={invoiceId}
          currency={invoice.currencyCode}
          totalPayableInPence={totalPayableInPence}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={refundModal}>
        <RefundInvoiceModal
          invoiceId={invoiceId}
          currency={invoice.currencyCode}
          totalRefundableInPence={patientPayer?.balance?.paid ?? 0}
          open={refundModal}
          onClose={() => setRefundModal(false)}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={!!(printerError ?? fetchError)}>
        <PrintErrorModal
          error={printerError ?? fetchError}
          onClose={resetPrinterErrors}
          onUseLaser={() => {
            resetPrinterErrors();
            setPrintLaserReceipt(true);
          }}
          onRetry={async () => {
            resetPrinterErrors();
            await invoiceActions.printInvoice.handlePrintReceipt();
          }}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={printPermissionsModal}>
        <PrintPermissionsModal
          onClose={() => setPrintPermissionModal(false)}
          onConnect={async () => {
            setPrintPermissionModal(false);
            await printReceipt();
          }}
          onUseLaser={() => {
            setPrintPermissionModal(false);
            setPrintLaserReceipt(true);
          }}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={paymentHistoryModal}>
        <PaymentHistoryModal
          invoiceId={invoiceId}
          currency={invoice.currencyCode}
          open={paymentHistoryModal}
          onClose={() => setPaymentHistoryModal(false)}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={printPayerSelectionModal}>
        <PrintPayerSelectionModal
          payers={invoicePayers}
          onClose={() => setPrintPayerSelectionModal(false)}
          onSelection={(payerNames) => {
            setPrintPayerNames(payerNames);
            setPrintInvoice(true);
          }}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={printInvoice}>
        <PrintableInvoice
          invoiceId={invoiceId}
          payerNames={printPayerNames}
          onAfterPrint={() => setPrintInvoice(false)}
        />
      </ConditionalDisplay>
      <ConditionalDisplay condition={printLaserReceipt}>
        <PrintableLaserReceipt
          invoiceId={invoiceId}
          onAfterPrint={() => setPrintLaserReceipt(false)}
        />
      </ConditionalDisplay>
    </Grid>
  );
};
